import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Download,
  CreditCard,
  CalendarDays,
  MapPin,
  ChevronDown,
  Pause,
  X,
  Plus,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import space1 from "@/assets/space-1.webp";
import space2 from "@/assets/space-2.webp";
import space3 from "@/assets/space-3.webp";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/subscriptions")({
  head: () => ({
    meta: [
      { title: "Subscriptions & billing — Spayzo" },
      {
        name: "description",
        content:
          "Manage your recurring space subscriptions and download invoices for each one.",
      },
    ],
  }),
  component: BillingPage,
});

type Status = "active" | "paused" | "cancelled";

type Invoice = {
  id: string;
  date: string;
  period: string;
  amount: number;
  status: "paid" | "refunded";
};

type SpaceSubscription = {
  id: string;
  space: string;
  location: string;
  image: string;
  plan: string;
  amount: number;
  cadence: "month" | "week";
  hoursIncluded: string;
  nextRenewal: string;
  startedOn: string;
  paymentMethod: string;
  status: Status;
  invoices: Invoice[];
};

const SUBS: SpaceSubscription[] = [
  {
    id: "s1",
    space: "Sunlit Creative Studio",
    location: "SoHo, New York",
    image: space1,
    plan: "Weekly Resident",
    amount: 320,
    cadence: "month",
    hoursIncluded: "16 hours / month",
    nextRenewal: "Jun 01, 2026",
    startedOn: "Jan 12, 2026",
    paymentMethod: "Visa ending 4242",
    status: "active",
    invoices: [
      { id: "INV-S1-005", date: "May 01, 2026", period: "May 2026", amount: 320, status: "paid" },
      { id: "INV-S1-004", date: "Apr 01, 2026", period: "Apr 2026", amount: 320, status: "paid" },
      { id: "INV-S1-003", date: "Mar 01, 2026", period: "Mar 2026", amount: 320, status: "paid" },
      { id: "INV-S1-002", date: "Feb 01, 2026", period: "Feb 2026", amount: 320, status: "paid" },
    ],
  },
  {
    id: "s2",
    space: "The Glass Loft",
    location: "Williamsburg, Brooklyn",
    image: space2,
    plan: "Evening Pass",
    amount: 180,
    cadence: "month",
    hoursIncluded: "8 evening hours / month",
    nextRenewal: "Jun 14, 2026",
    startedOn: "Feb 14, 2026",
    paymentMethod: "Mastercard ending 1881",
    status: "active",
    invoices: [
      { id: "INV-S2-004", date: "May 14, 2026", period: "May 2026", amount: 180, status: "paid" },
      { id: "INV-S2-003", date: "Apr 14, 2026", period: "Apr 2026", amount: 180, status: "paid" },
      { id: "INV-S2-002", date: "Mar 14, 2026", period: "Mar 2026", amount: 180, status: "paid" },
    ],
  },
  {
    id: "s3",
    space: "Atelier Nord",
    location: "Greenpoint, Brooklyn",
    image: space3,
    plan: "Day Pass",
    amount: 90,
    cadence: "week",
    hoursIncluded: "1 full day / week",
    nextRenewal: "—",
    startedOn: "Nov 02, 2025",
    paymentMethod: "Visa ending 4242",
    status: "paused",
    invoices: [
      { id: "INV-S3-006", date: "Apr 28, 2026", period: "Wk 17", amount: 90, status: "paid" },
      { id: "INV-S3-005", date: "Apr 21, 2026", period: "Wk 16", amount: 90, status: "refunded" },
      { id: "INV-S3-004", date: "Apr 14, 2026", period: "Wk 15", amount: 90, status: "paid" },
    ],
  },
];

function BillingPage() {
  const { t } = useTranslation();
  const monthlyTotal = SUBS.filter((s) => s.status === "active").reduce(
    (sum, s) => sum + (s.cadence === "month" ? s.amount : s.amount * 4),
    0,
  );

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <SiteHeader />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">
              <Link to="/dashboard" className="hover:text-foreground">
                {t("pages.subscriptions.breadcrumbDashboard")}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-foreground">{t("pages.subscriptions.breadcrumbCurrent")}</span>
            </p>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">
              {t("pages.subscriptions.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("pages.subscriptions.subtitle")}
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/discover">
              <Plus className="h-4 w-4" />
              {t("pages.subscriptions.findSpace")}
            </Link>
          </Button>
        </div>

        {/* Summary */}
        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryCard label={t("pages.subscriptions.activeCount")} value={String(SUBS.filter((s) => s.status === "active").length)} />
          <SummaryCard label={t("pages.subscriptions.monthlySpend")} value={<Money value={monthlyTotal} />} />
          <SummaryCard label={t("pages.subscriptions.defaultPayment")} value={t("pages.subscriptions.visa4242")} icon={<CreditCard className="h-4 w-4" />} />
        </div>

        {/* Subscriptions */}
        <section className="mt-10 space-y-4">
          {SUBS.map((sub) => (
            <SubscriptionCard key={sub.id} sub={sub} />
          ))}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <Card className="shadow-soft">
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-1 font-display text-2xl font-bold">{value}</p>
        </div>
        {icon && <div className="text-muted-foreground">{icon}</div>}
      </CardContent>
    </Card>
  );
}

function SubscriptionCard({ sub }: { sub: SpaceSubscription }) {
  const { t } = useTranslation();
  const spaceName = t(`pages.subscriptions.demo.${sub.id}.space`, { defaultValue: sub.space });
  const location = t(`pages.subscriptions.demo.${sub.id}.location`, { defaultValue: sub.location });
  const planName = t(`pages.subscriptions.demo.${sub.id}.plan`, { defaultValue: sub.plan });
  const hours = t(`pages.subscriptions.demo.${sub.id}.hours`, { defaultValue: sub.hoursIncluded });
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>(sub.status);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const confirmPause = () => {
    setStatus("paused");
    setPauseOpen(false);
    toast.success(t("pages.subscriptions.pausedToast", { space: spaceName }));
  };

  const confirmCancel = () => {
    setStatus("cancelled");
    setCancelOpen(false);
    toast.success(t("pages.subscriptions.cancelledToast", { space: spaceName }));
  };

  return (
    <Card className="overflow-hidden shadow-soft">
      <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5 sm:space-y-0">
        <img
          src={sub.image}
          alt={spaceName}
          className="h-32 w-full rounded-xl object-cover sm:h-20 sm:w-28"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="font-display text-lg">{spaceName}</CardTitle>
            <StatusPill status={status} />
          </div>
          <CardDescription className="mt-1 flex items-center gap-1 text-xs">
            <MapPin className="h-3 w-3" />
            {location}
          </CardDescription>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>
              <Badge variant="secondary" className="rounded-full">
                {planName}
              </Badge>
            </span>
            <span>{hours}</span>
            {status === "active" && (
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3.5 w-3.5" />
                {t("pages.subscriptions.renews", { date: sub.nextRenewal })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-end">
          <div className="text-end">
            <p className="font-display text-2xl font-bold"><Riyal /> {sub.amount}</p>
            <p className="text-xs text-muted-foreground">{sub.cadence === "month" ? t("pages.subscriptions.perMonth") : t("pages.subscriptions.perWeek")}</p>
          </div>
          <div className="flex gap-2">
            {status === "active" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPauseOpen(true)}
                className="active:scale-95 transition-transform"
              >
                <Pause className="h-4 w-4" />
                {t("pages.subscriptions.pause")}
              </Button>
            ) : status === "paused" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStatus("active");
                  toast.success(t("pages.subscriptions.resumedToast", { space: spaceName }));
                }}
                className="active:scale-95 transition-transform"
              >
                {t("pages.subscriptions.resume")}
              </Button>
            ) : null}
            {status !== "cancelled" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCancelOpen(true)}
                className="text-destructive hover:text-destructive active:scale-95 transition-transform"
              >
                <X className="h-4 w-4" />
                {t("pages.subscriptions.cancel")}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <AlertDialog open={pauseOpen} onOpenChange={setPauseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.subscriptions.pauseTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.subscriptions.pauseBody", { space: spaceName, date: sub.nextRenewal })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.subscriptions.keepActive")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPause}>
              {t("pages.subscriptions.pauseConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.subscriptions.cancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.subscriptions.cancelBody", { plan: planName, space: spaceName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.subscriptions.keepSubscription")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("pages.subscriptions.cancelConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CardContent className="border-t bg-muted/30 p-0">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between px-5 py-3 text-sm font-medium hover:bg-muted/60"
        >
          <span>
            {t("pages.subscriptions.billingHistory")}{" "}
            <span className="ms-1 text-muted-foreground">({sub.invoices.length})</span>
          </span>
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
          />
        </button>

        {open && (
          <div className="border-t bg-card px-3 py-3 sm:px-5">
            {/* Desktop */}
            <div className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.subscriptions.invoice")}</TableHead>
                    <TableHead>{t("pages.subscriptions.date")}</TableHead>
                    <TableHead>{t("pages.subscriptions.period")}</TableHead>
                    <TableHead className="text-end">{t("pages.subscriptions.amount")}</TableHead>
                    <TableHead>{t("pages.subscriptions.statusLabel")}</TableHead>
                    <TableHead className="text-end">{t("pages.subscriptions.receipt")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sub.invoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">{inv.id}</TableCell>
                      <TableCell className="text-muted-foreground">{inv.date}</TableCell>
                      <TableCell className="text-muted-foreground">{inv.period}</TableCell>
                      <TableCell className="text-end font-medium"><Riyal /> {inv.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <InvoiceStatus status={inv.status} />
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                          {t("pages.subscriptions.pdf")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile */}
            <ul className="divide-y sm:hidden">
              {sub.invoices.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{inv.id}</p>
                    <p className="text-xs text-muted-foreground">
                      {inv.date} · {inv.period}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium"><Riyal /> {inv.amount.toFixed(2)}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-3 flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
              <span>{t("pages.subscriptions.paidWith", { method: sub.paymentMethod })}</span>
              <span>{t("pages.subscriptions.startedOn", { date: sub.startedOn })}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }: { status: Status }) {
  const { t } = useTranslation();
  const map = {
    active: { label: t("pages.subscriptions.status.active"), className: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
    paused: { label: t("pages.subscriptions.status.paused"), className: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
    cancelled: { label: t("pages.subscriptions.status.cancelled"), className: "bg-destructive/10 text-destructive" },
  };
  const { label, className } = map[status];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        className,
      )}
    >
      {label}
    </span>
  );
}

function InvoiceStatus({ status }: { status: Invoice["status"] }) {
  const { t } = useTranslation();
  if (status === "paid") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
        <Check className="h-3 w-3" />
        {t("pages.subscriptions.invoiceStatus.paid")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
      {t("pages.subscriptions.invoiceStatus.refunded")}
    </span>
  );
}
